/**
 * Framework-free model of an image being edited: original pixels, committed operations, undo history,
 * the shared selection and live previews. The editor's React hook wraps it; unit tests drive it directly.
 */
import {Bounds, getBrushDiameter, PixelBuffer, Point} from './imageEditing';
import {
   combineMasks,
   createMagicWandMask,
   createRectangleMask,
   getMaskBounds,
   invertMask,
   SelectionMask,
   SelectionMode
} from './selectionMask';
import {
   AdjustmentKind,
   AdjustOperation,
   applyOperation,
   applyOperations,
   clonePixels,
   EditOperation,
   EditOperationType,
   EraseStrokeOperation,
   eraseDabs,
   getStrokeDabs
} from './editOperations';
import {createHistory, DocumentHistory, historyReducer, HistoryActionType} from './documentHistory';

/** Wand tolerance used until the operator moves the slider. */
export const DEFAULT_TOLERANCE = 32;

/** A brightness or contrast change previewed live until it is committed as one operation. */
export type PendingAdjustment = Pick<AdjustOperation, 'kind' | 'amount'>;

/** Runs a callback on the next animation frame; the default runs it immediately (used by tests). */
export type FrameScheduler = (callback: () => void) => void;

/** The latest wand click, kept so tolerance changes can re-run it against the selection that preceded it. */
interface WandSeed {
   point: Point;
   mode: SelectionMode;
   baseSelection: SelectionMask | null;
}

/** A brush stroke being drawn; its dabs are already erased from the committed pixels. */
type ActiveStroke = Omit<EraseStrokeOperation, 'type'>;

/** Runs a callback straight away. */
const runImmediately: FrameScheduler = callback => callback();

export class ImageDocument {
   private readonly onChange: () => void;
   private readonly scheduleFrame: FrameScheduler;
   private original: PixelBuffer | null = null;
   private committed: PixelBuffer | null = null;
   private preview: PixelBuffer | null = null;
   private history: DocumentHistory = createHistory();
   private selectionPreview: SelectionMask | null | undefined = undefined;
   private wandSeed: WandSeed | null = null;
   private stroke: ActiveStroke | null = null;
   private pendingAdjustment: PendingAdjustment | null = null;
   private tolerance = DEFAULT_TOLERANCE;
   private pixelsVersion = 0;
   private deferredWork: (() => void) | null = null;

   /** `onChange` fires after every visible change; `scheduleFrame` coalesces expensive live previews. */
   constructor(onChange: () => void, scheduleFrame: FrameScheduler = runImmediately) {
      this.onChange = onChange;
      this.scheduleFrame = scheduleFrame;
   }

   /** True once pixels have been loaded; every editing action is ignored before that. */
   isReady(): boolean {
      return this.committed !== null;
   }

   /** Pixels to display and export: the committed edits plus any live adjustment preview. */
   getPixels(): PixelBuffer | null {
      return this.preview ?? this.committed;
   }

   /** Increases whenever the content of getPixels() changes, so the canvas knows to redraw. */
   getPixelsVersion(): number {
      return this.pixelsVersion;
   }

   /** The selection to show: a rectangle being dragged, otherwise the current selection (null = whole image). */
   getSelection(): SelectionMask | null {
      return this.selectionPreview !== undefined ? this.selectionPreview : this.history.present.selection;
   }

   /** Whether a committed selection exists, i.e. selection commands have something to act on. */
   hasSelection(): boolean {
      return this.history.present.selection !== null;
   }

   /** Bounding box of the committed selection in native pixels, or null without a selection. */
   getSelectionBounds(): Bounds | null {
      const selection = this.history.present.selection;
      return selection ? getMaskBounds(selection) : null;
   }

   /** The wand tolerance (0–255) used for the latest and the next wand click. */
   getTolerance(): number {
      return this.tolerance;
   }

   /** The brightness or contrast change currently previewed, or null. */
   getPendingAdjustment(): PendingAdjustment | null {
      return this.pendingAdjustment;
   }

   /** Whether undo would change anything: a pending adjustment to discard or an earlier step. */
   canUndo(): boolean {
      return this.pendingAdjustment !== null || this.history.past.length > 0;
   }

   /** Starts editing new pixels with an empty history and no selection. */
   load(pixels: PixelBuffer): void {
      this.deferredWork = null;
      this.original = pixels;
      this.committed = clonePixels(pixels);
      this.preview = null;
      this.history = createHistory();
      this.selectionPreview = undefined;
      this.wandSeed = null;
      this.stroke = null;
      this.pendingAdjustment = null;
      this.notify(true);
   }

   /** Shows the selection a rectangle drag would produce, without adding an undo step. */
   previewRectangle(from: Point, to: Point, mode: SelectionMode): void {
      this.defer(() => {
         const committed = this.committed;
         if (!committed) {
            return;
         }
         const rectangle = createRectangleMask(committed.width, committed.height, from, to);
         this.selectionPreview = combineMasks(this.history.present.selection, rectangle, mode);
         this.notify();
      });
   }

   /** Drops a rectangle preview, e.g. after a modifier click that did not drag. */
   cancelSelectionPreview(): void {
      this.flush();
      if (this.selectionPreview !== undefined) {
         this.selectionPreview = undefined;
         this.notify();
      }
   }

   /** Commits the rectangle spanned by two image points as a selection step. */
   selectRectangle(from: Point, to: Point, mode: SelectionMode): void {
      const committed = this.prepareCommand();
      if (!committed) {
         return;
      }
      this.selectionPreview = undefined;
      const rectangle = createRectangleMask(committed.width, committed.height, from, to);
      this.pushSelection(combineMasks(this.history.present.selection, rectangle, mode));
   }

   /** Selects the similar-coloured area around a click; tolerance changes keep tuning it until the next command. */
   selectWithWand(point: Point, mode: SelectionMode): void {
      const committed = this.prepareCommand();
      if (!committed) {
         return;
      }
      this.wandSeed = {point, mode, baseSelection: this.history.present.selection};
      this.pushSelection(this.runWand(this.wandSeed, committed));
   }

   /** Sets the wand tolerance and re-runs the latest wand click with it, amending rather than adding a step. */
   setTolerance(tolerance: number): void {
      this.tolerance = tolerance;
      this.notify();
      const seed = this.wandSeed;
      if (!seed) {
         return;
      }
      this.defer(() => {
         const committed = this.committed;
         if (!committed) {
            return;
         }
         const snapshot = {...this.history.present, selection: this.runWand(seed, committed)};
         this.history = historyReducer(this.history, {type: HistoryActionType.Amend, snapshot});
         this.notify();
      });
   }

   /** Clears the selection so commands apply to the whole image. */
   deselect(): void {
      if (!this.prepareCommand()) {
         return;
      }
      const hadPreview = this.selectionPreview !== undefined;
      this.selectionPreview = undefined;
      if (this.history.present.selection) {
         this.pushSelection(null);
      } else if (hadPreview) {
         this.notify();
      }
   }

   /** Swaps the selected and unselected areas. */
   invertSelection(): void {
      if (!this.prepareCommand()) {
         return;
      }
      const selection = this.history.present.selection;
      if (selection) {
         this.pushSelection(invertMask(selection));
      }
   }

   /**
    * Makes the selected pixels transparent. Without a selection it does nothing, so the image is never wiped,
    * and erasing the just-erased selection again (Delete pressed twice or held) adds no empty undo steps.
    */
   eraseSelection(): void {
      const committed = this.prepareCommand();
      const mask = this.history.present.selection;
      const operations = this.history.present.operations;
      const lastOperation = operations[operations.length - 1];
      const alreadyErased = lastOperation?.type === EditOperationType.EraseSelection && lastOperation.mask === mask;
      if (!committed || !mask || alreadyErased) {
         return;
      }
      const operation: EditOperation = {type: EditOperationType.EraseSelection, mask};
      applyOperation(committed, operation);
      this.pushOperation(operation, committed);
   }

   /** Starts an erase stroke; brushSize is a percentage of the image's shorter edge. */
   beginStroke(point: Point, brushSize: number): void {
      const committed = this.prepareCommand();
      if (!committed) {
         return;
      }
      const stroke: ActiveStroke = {
         points: [point],
         diameter: getBrushDiameter(committed.width, committed.height, brushSize),
         clip: this.history.present.selection
      };
      this.stroke = stroke;
      eraseDabs(committed, [point], stroke.diameter, stroke.clip);
      this.notify(true);
   }

   /** Continues the active stroke to a point, filling the gap since the previous point. */
   extendStroke(point: Point): void {
      const {stroke, committed} = this;
      if (!stroke || !committed) {
         return;
      }
      const previous = stroke.points[stroke.points.length - 1];
      stroke.points.push(point);
      // The first dab of the segment is the previous point, which is already erased.
      eraseDabs(committed, getStrokeDabs([previous, point], stroke.diameter).slice(1), stroke.diameter, stroke.clip);
      this.notify(true);
   }

   /** Finishes the active stroke as one undo step; its pixels are already erased. */
   endStroke(): void {
      const {stroke, committed} = this;
      if (!stroke || !committed) {
         return;
      }
      this.stroke = null;
      this.pushOperation({
         type: EditOperationType.EraseStroke,
         points: stroke.points,
         diameter: stroke.diameter,
         clip: stroke.clip
      }, committed);
   }

   /** Previews a brightness or contrast change inside the selection (0 removes it) and ends wand tolerance tuning. */
   setPendingAdjustment(kind: AdjustmentKind, amount: number): void {
      if (!this.committed) {
         return;
      }
      this.wandSeed = null;
      this.pendingAdjustment = amount === 0 ? null : {kind, amount};
      this.notify();
      this.defer(() => this.renderPreview());
   }

   /** Commits a pending adjustment as one undo step and ends tolerance tuning of the latest wand click. */
   commitPending(): void {
      this.flush();
      this.wandSeed = null;
      const {pendingAdjustment, preview} = this;
      if (!pendingAdjustment || !preview) {
         return;
      }
      this.pendingAdjustment = null;
      this.preview = null;
      // The preview already holds the committed pixels plus this adjustment, so it becomes the committed buffer.
      this.pushOperation(this.createAdjustOperation(pendingAdjustment), preview);
   }

   /** Discards a pending adjustment if there is one, otherwise steps back once. Ignored while a stroke is drawn. */
   undo(): void {
      this.flush();
      const original = this.original;
      if (this.stroke || !original) {
         return;
      }
      this.wandSeed = null;
      this.selectionPreview = undefined;
      if (this.pendingAdjustment) {
         this.pendingAdjustment = null;
         this.preview = null;
         this.notify(true);
         return;
      }
      if (this.history.past.length === 0) {
         return;
      }
      const replacedOperations = this.history.present.operations;
      this.history = historyReducer(this.history, {type: HistoryActionType.Undo});
      const operationsChanged = this.history.present.operations !== replacedOperations;
      if (operationsChanged) {
         this.committed = applyOperations(original, this.history.present.operations);
      }
      this.notify(operationsChanged);
   }

   /**
    * Readies the document for a discrete command: records a stroke whose mouse release never arrived, runs
    * deferred previews and commits pending work. Returns the committed pixels.
    */
   private prepareCommand(): PixelBuffer | null {
      this.endStroke();
      this.commitPending();
      return this.committed;
   }

   /** Re-runs a wand click with the current tolerance and combines it with the selection that preceded it. */
   private runWand(seed: WandSeed, pixels: PixelBuffer): SelectionMask | null {
      return combineMasks(seed.baseSelection, createMagicWandMask(pixels, seed.point, this.tolerance), seed.mode);
   }

   /** Rebuilds the preview pixels from the committed pixels and the pending adjustment, reusing the buffer. */
   private renderPreview(): void {
      const {committed, pendingAdjustment} = this;
      if (!committed) {
         return;
      }
      if (pendingAdjustment) {
         const preview = this.preview ?? clonePixels(committed);
         preview.data.set(committed.data);
         applyOperation(preview, this.createAdjustOperation(pendingAdjustment));
         this.preview = preview;
      } else {
         this.preview = null;
      }
      this.notify(true);
   }

   /** Builds the adjustment operation for the current selection. */
   private createAdjustOperation(adjustment: PendingAdjustment): AdjustOperation {
      return {
         type: EditOperationType.Adjust,
         kind: adjustment.kind,
         amount: adjustment.amount,
         clip: this.history.present.selection
      };
   }

   /** Adds a selection step. */
   private pushSelection(selection: SelectionMask | null): void {
      this.history = historyReducer(this.history, {
         type: HistoryActionType.Push,
         snapshot: {...this.history.present, selection}
      });
      this.notify();
   }

   /** Adds an operation step whose result is already rendered into `rendered`. */
   private pushOperation(operation: EditOperation, rendered: PixelBuffer): void {
      this.committed = rendered;
      this.history = historyReducer(this.history, {
         type: HistoryActionType.Push,
         snapshot: {...this.history.present, operations: [...this.history.present.operations, operation]}
      });
      this.notify(true);
   }

   /** Runs work on the next frame, replacing work already waiting for it. */
   private defer(work: () => void): void {
      // Latest work wins. Different kinds of preview never compete, because each belongs to one tool
      // and every command or tool change flushes first.
      const needsFrame = this.deferredWork === null;
      this.deferredWork = work;
      if (needsFrame) {
         this.scheduleFrame(() => this.flush());
      }
   }

   /** Runs deferred work now, so the next command sees an up-to-date preview. */
   private flush(): void {
      const work = this.deferredWork;
      this.deferredWork = null;
      work?.();
   }

   /** Tells the owner that something visible changed. */
   private notify(pixelsChanged = false): void {
      if (pixelsChanged) {
         this.pixelsVersion += 1;
      }
      this.onChange();
   }
}
